import { useCallback, useMemo, useState } from "react";

import { useGetBillingShops, useGetMe, useGetStore } from "@/api";
import type {
  SettingsBillingView,
  SettingsTabId,
} from "@/components/Templates/Settings/Settings.template.types";
import { useInstagramConnection } from "@/hooks/instagram/useInstagramConnection";
import { useSettingsDanger } from "@/hooks/settings/useSettingsDanger";
import { useSettingsGeneral } from "@/hooks/settings/useSettingsGeneral";
import { useSettingsShipping } from "@/hooks/settings/useSettingsShipping";
import { useSettingsSocials } from "@/hooks/settings/useSettingsSocials";
import { useSettingsTeam } from "@/hooks/settings/useSettingsTeam";
import { toAmount } from "@/lib/billing/constants";
import { formatPersianNumber } from "@/lib/format/persian";
import {
  getRoleDefinition,
  getVisibleTabsForRole,
  resolveShopRoleKey,
} from "@/lib/settings/access";
import { useSessionStore } from "@/stores/session.store";

export function useSettingsPage(initialTab?: SettingsTabId) {
  const activeStoreId = useSessionStore((state) => state.activeStoreId);

  const { data: meData } = useGetMe();
  const me = meData?.status === 200 ? meData.data.data : undefined;
  const membership = me?.stores.find((store) => store.id === activeStoreId);

  // The role comes from the per-store membership; the backend enforces the same matrix.
  const roleKey = useMemo(
    () => resolveShopRoleKey(membership?.role_name, me?.roles),
    [membership?.role_name, me?.roles],
  );
  const visibleTabs = useMemo(() => getVisibleTabsForRole(roleKey), [roleKey]);
  const isOwner = roleKey === "owner";

  const [activeTab, setActiveTab] = useState<SettingsTabId>(initialTab ?? "general");
  const resolvedActiveTab = visibleTabs.includes(activeTab)
    ? activeTab
    : (visibleTabs[0] ?? "general");
  const canSee = (tab: SettingsTabId) => activeStoreId !== null && visibleTabs.includes(tab);

  const { data: storeData } = useGetStore(activeStoreId ?? 0, {
    query: { enabled: activeStoreId !== null && visibleTabs.length > 0 },
  });
  const store = storeData?.status === 200 ? storeData.data.data : undefined;

  const general = useSettingsGeneral(activeStoreId, store, isOwner);
  const team = useSettingsTeam(activeStoreId, canSee("team"), me?.id);
  const shipping = useSettingsShipping(activeStoreId, canSee("shipping"));
  const socials = useSettingsSocials(activeStoreId, canSee("socials"));
  const instagram = useInstagramConnection(activeStoreId, canSee("instagram"), isOwner);
  const danger = useSettingsDanger(activeStoreId, store);

  const { data: billingShopsData, isLoading: isBillingLoading } = useGetBillingShops({
    query: { enabled: canSee("billing") },
  });
  const billing: SettingsBillingView | null = useMemo(() => {
    const shops = billingShopsData?.status === 200 ? billingShopsData.data.data : [];
    const shop = shops.find((item) => item.store_id === activeStoreId);
    if (!shop) return null;
    const debt = toAmount(shop.outstanding_debt);
    return {
      totalCommissionLabel: formatPersianNumber(toAmount(shop.total_commission)),
      outstandingDebtLabel: formatPersianNumber(debt),
      hasDebt: debt > 0,
      isRestricted: shop.is_restricted,
      onlinePaymentEnabled: shop.online_payment_enabled,
    };
  }, [activeStoreId, billingShopsData]);

  const onTabChange = useCallback((value: string) => setActiveTab(value as SettingsTabId), []);

  return {
    hasActiveStore: activeStoreId !== null,
    hasAccess: visibleTabs.length > 0,
    roleKey,
    roleLabel: getRoleDefinition(roleKey).labelKey,
    tabCountLabel: formatPersianNumber(visibleTabs.length),
    activeTab: resolvedActiveTab,
    visibleTabs,
    onTabChange,
    general,
    team,
    shipping,
    socials,
    instagram,
    billing: { billing, isLoading: isBillingLoading },
    danger,
  };
}
